import _defineProperty from "/app/node_modules/.pnpm/next@12.3.3_@babel+core@7.14.3_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Cell, Text } from '@vinted/web-ui';
import { escape, escapeRegExp, isEqual, kebabCase, noop } from 'lodash';
import uuid from 'uuid';
import { Waypoint } from 'react-waypoint';
import List from 'components/List';
import useTracking from 'hooks/useTracking';
import { SearchSuggestionType } from 'constants/search';
import { viewSearchSuggestionEvent, viewSearchSuggestionsEvent } from 'libs/common/event-tracker/events';
import { actions } from 'state/search-suggestions/slice';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";

const SearchSuggestions = _ref => {
  let {
    query,
    items,
    highlightedIndex,
    searchSessionId,
    globalSearchSessionId,
    suggestionUrl,
    setSuggestionsSessionId,
    suggestionsListId,
    onSuggestionClick = noop
  } = _ref;
  const {
    track
  } = useTracking();
  const dispatch = useDispatch();
  const prevItems = useRef();
  const trackedQuery = useRef(query);
  const suggestionsSessionId = useRef(null);
  const shouldAddViewSearchSuggestionEvent = useSelector(getIsFeatureSwitchEnabled('web_add_view_search_suggestion_event'));
  useEffect(() => {
    suggestionsSessionId.current = uuid.v4();
    setSuggestionsSessionId(suggestionsSessionId.current);
  }, [setSuggestionsSessionId]);
  useEffect(() => {
    trackedQuery.current = query;
  }, [query]);
  useEffect(() => {
    if (!items.length || isEqual(prevItems.current, items)) return;
    track(viewSearchSuggestionsEvent({
      suggestions: items.map(item => item.query),
      query: trackedQuery.current,
      suggestionsSessionId: suggestionsSessionId.current
    }));
    prevItems.current = items;
  }, [track, items]);
  useEffect(() => () => {
    dispatch(actions.clearSuggestions());
  }, [dispatch]);

  const handleItemClick = (suggestion, index) => event => {
    const scopedSuggestions = items.filter(item => item.type === SearchSuggestionType.Scoped);
    onSuggestionClick(suggestion, index, scopedSuggestions.length, event);
  };

  const getSuggestionKey = suggestion => {
    if (suggestion.type === SearchSuggestionType.Scoped) {
      return [suggestion.id, kebabCase(suggestion.subtitle)].join('-');
    }

    return suggestion.id;
  };

  const getHighlightedTitle = suggestionTitle => {
    const escapedQuery = escape(query);
    const escapedSuggestionTitle = escape(suggestionTitle);
    return escapedSuggestionTitle.replace(new RegExp("^".concat(escapeRegExp(escapedQuery)), 'i'), '<b>$&</b>');
  };

  const renderSuggestion = (suggestion, index) => {
    const cellProps = {
      type: Cell.Type.Navigating,
      url: suggestionUrl(suggestion),
      highlighted: highlightedIndex === index,
      testId: "suggestion-".concat(suggestion.id)
    };

    switch (suggestion.type) {
      case SearchSuggestionType.Scoped:
        return /*#__PURE__*/_createElement(Cell, _objectSpread(_objectSpread({}, cellProps), {}, {
          onClick: handleItemClick(suggestion, index),
          key: getSuggestionKey(suggestion)
        }), /*#__PURE__*/_jsx(Text, {
          theme: "amplified",
          text: getHighlightedTitle(suggestion.title),
          html: true,
          highlight: true,
          highlightTheme: "muted"
        }), ' ', /*#__PURE__*/_jsx(Text, {
          text: suggestion.subtitle,
          type: Text.Type.Subtitle,
          theme: "primary",
          as: "span"
        }));

      case SearchSuggestionType.Multiword:
      case SearchSuggestionType.Brand:
      case SearchSuggestionType.Catalog:
        {
          return /*#__PURE__*/_createElement(Cell, _objectSpread(_objectSpread({}, cellProps), {}, {
            onClick: handleItemClick(suggestion, index),
            key: getSuggestionKey(suggestion)
          }), /*#__PURE__*/_jsx(Text, {
            theme: "amplified",
            text: getHighlightedTitle(suggestion.title),
            html: true,
            highlight: true,
            highlightTheme: "muted"
          }));
        }

      case SearchSuggestionType.Fallback:
        return /*#__PURE__*/_createElement(Cell, _objectSpread(_objectSpread({}, cellProps), {}, {
          onClick: handleItemClick(suggestion, index),
          title: suggestion.title,
          key: getSuggestionKey(suggestion)
        }));

      default:
        return null;
    }
  };

  const handleItemView = (item, index) => () => {
    const isFrontendGeneratedTextSelected = item.type === SearchSuggestionType.Fallback;
    const trackingArgs = {
      frontendGeneratedTextSelected: isFrontendGeneratedTextSelected,
      globalSearchSessionId,
      query: item.query,
      searchSessionId: searchSessionId || null,
      selectedSuggestionText: isFrontendGeneratedTextSelected ? item.query : item.title,
      suggestionPosition: index + 1,
      suggestionsListId,
      suggestionsSessionId: suggestionsSessionId.current
    };

    if ('params' in item) {
      trackingArgs.params = item.params;
    }

    track(viewSearchSuggestionEvent(trackingArgs));
  };

  const renderSuggestions = suggestions => /*#__PURE__*/_jsx(List, {
    children: suggestions.map((suggestion, index) => /*#__PURE__*/_jsx(Waypoint, {
      onEnter: handleItemView(suggestion, index),
      children: /*#__PURE__*/_jsx("div", {
        children: renderSuggestion(suggestion, index)
      })
    }, getSuggestionKey(suggestion)))
  });

  if (!items.length) return null;
  return /*#__PURE__*/_jsx(Cell, {
    styling: Cell.Styling.Tight,
    children: shouldAddViewSearchSuggestionEvent ? renderSuggestions(items) : /*#__PURE__*/_jsx(List, {
      children: items.map((item, index) => renderSuggestion(item, index))
    })
  });
};

export default SearchSuggestions;